/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable } from '@angular/core';
import { isEmpty, isEqual, xorWith } from 'lodash-es';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoggingService } from '../services/logging.service';
import { TriggerTemplate } from '../models/client-settings';
import { TeamInfo } from '../models/team-info';
import { HttpStatus } from '../enums/http-status';
import { ApiService } from './api/api.service';
import { AuthService } from './auth.service';
import { BaseAuthenticatedPollingService } from './base/base-authenticated-polling-service';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root',
})
export class ServicesService extends BaseAuthenticatedPollingService {
  private readonly mappingsToLog: number = 5;
  public serviceApplicationMappings$: BehaviorSubject<TeamInfo[]> = new BehaviorSubject<TeamInfo[]>(undefined);

  public constructor(
    private readonly apiService: ApiService,
    authService: AuthService,
    private readonly loggingService: LoggingService,
    protected readonly configurationService: ConfigurationService,
  ) {
    super(authService, configurationService);
  }

  public async stopPollingAction(): Promise<void> {
    this.serviceApplicationMappings$.next(undefined);
  }

  public getServiceCallTemplates(teamId: string): Observable<TriggerTemplate[]> {
    return of(this.serviceApplicationMappings$.value?.find((x) => x.teamId === teamId)?.templates || []);
  }

  public async pollingAction(): Promise<void> {
    this.apiService
      .getServicesMapping()
      .pipe(
        catchError((err) => {
          this.loggingService.Error(JSON.stringify(err));
          const accessDenied =
            err.httpStatus === HttpStatus.NOT_FOUND_404 ||
            (err.httpStatus === HttpStatus.FORBIDDEN_403 && err.error === 'Feature Assistant is disabled');
          if (accessDenied) {
            return of([]);
          }

          this.handlePollingError();
          return of(null);
        }),
      )
      .subscribe((mapping: TeamInfo[]) => {
        if (mapping) {
          this.handlePollingSuccess();
        }
        let mappingsChanged = true;
        try {
          mappingsChanged = !isEmpty(xorWith(this.serviceApplicationMappings$.value, mapping, isEqual));
        } catch (err) {
          this.loggingService.Warning(`Service Application Mappings Comparison failed:\n${JSON.stringify(err?.message)}`);
          mappingsChanged = isEqual(this.serviceApplicationMappings$.value, mapping);
        }
        if (mappingsChanged) {
          this.loggingService.Info(
            `Service Application Mappings:\n${JSON.stringify(mapping?.slice(0, this.mappingsToLog))} ${
              mapping?.length > this.mappingsToLog ? `\nand ${mapping.length - this.mappingsToLog} more` : ''
            }`,
          );
        }
        this.serviceApplicationMappings$.next(mapping);
      });
  }

  public getTeamInfoByTeamId(teamId: string): TeamInfo {
    return this.serviceApplicationMappings$.value?.find((x) => x.teamId === teamId);
  }

  public getTeamInfoByAppId(appId: string): TeamInfo {
    return this.serviceApplicationMappings$.value?.find((x) => x.applicationId === appId);
  }
}
